import type { DenialDetail } from "@/api/client";

/**
 * L8 denial-mode banner.
 *
 * Rendered above the project-data form when the backend refuses
 * to generate documents because one or more `export_blocking`
 * mandatory laws are missing their consolidated text in the
 * `permitpilot.law_*` snapshot.
 *
 * The banner is intentionally loud — denial mode is the only path
 * where docgen flat-out refuses to run, and the architect needs to
 * see *why* (which instrument, what status) before they re-trigger.
 */
export function DenialBanner({ denial }: { denial: DenialDetail }) {
  return (
    <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-[0.9rem] text-red-800">
      <div className="mb-2 flex items-baseline gap-2">
        <span className="rounded bg-red-700 px-1.5 py-0.5 text-[0.7rem] font-semibold uppercase tracking-wide text-white">
          Pravna baza nije spremna
        </span>
        <span className="font-semibold">
          Generiranje je zaustavljeno radi cjelovitosti pravnog citiranja.
        </span>
      </div>
      <p className="mb-2 text-[0.85rem] text-red-900">{denial.message}</p>
      <p className="mb-1 text-[0.78rem] font-semibold text-red-900">
        Sljedeći obvezni propisi nemaju dostupan tekst u katalogu:
      </p>
      <ul className="ml-5 list-disc space-y-0.5 text-[0.82rem] text-red-900">
        {denial.missing.map((law) => (
          <li key={`${law.rule_id}:${law.instrument_id ?? "unresolved"}`}>
            <span className="font-medium">{law.short_title}</span>
            {" — "}
            <span className="italic">{law.official_title}</span>
            {law.reason === "no_text" ? (
              <span className="ml-1 text-red-700">(tekst nedostupan)</span>
            ) : (
              <span className="ml-1 text-red-700">(propis nije pronađen u katalogu)</span>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[0.78rem] text-red-700">
        Pokrenite <code className="rounded bg-red-100 px-1">tools/laws/sync_catalog.py</code>{" "}
        ili se obratite kustosu pravne baze prije ponovnog pokušaja.
      </p>
    </div>
  );
}
